/**
 * Sprint 2.7.7 Step 5 — Content & Keywords dashboard section.
 *
 * Three sub-cards:
 *   1. Keyword Universe — list of researched keywords with volume data
 *   2. Category Demand  — Google Trends YoY index + sparkline
 *   3. Content Briefs   — quota usage + brief list + generate modal
 *
 * Mounted as the "keywords" tab in DomainDetail.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import type {
  BusinessContext,
  KeywordVolumeEntry,
  CategoryTrend,
  ContentBrief,
  ContentBriefQuota,
} from '../types';
import { api } from '../api';
import { ContentBriefModal } from '../components/ContentBriefModal';
import { CopyForLlmButton } from '../components/CopyForLlmButton';

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  domainId: string;
  domain: string;
  tier: string;
}

// ── Main component ────────────────────────────────────────────────────────────

export function ContentAndKeywords({ domainId, domain, tier }: Props) {
  // Keywords state
  const [keywords, setKeywords] = useState<KeywordVolumeEntry[]>([]);
  const [keywordsLoading, setKeywordsLoading] = useState(true);
  const [keywordsError, setKeywordsError] = useState<string | null>(null);

  const [bizContext, setBizContext] = useState<BusinessContext | null>(null);

  // Trends state
  const [trend, setTrend] = useState<CategoryTrend | null>(null);
  const [trendLoading, setTrendLoading] = useState(true);
  const [trendNoData, setTrendNoData] = useState(false);

  // Content briefs state
  const [briefs, setBriefs] = useState<ContentBrief[]>([]);
  const [quota, setQuota] = useState<ContentBriefQuota | null>(null);
  const [briefsLoading, setBriefsLoading] = useState(true);
  const [briefsError, setBriefsError] = useState<string | null>(null);

  // Modal state
  const [generateOpen, setGenerateOpen] = useState(false);
  const [selectedBrief, setSelectedBrief] = useState<ContentBrief | null>(null);
  const [showDiscarded, setShowDiscarded] = useState(false);

  // Toast (local — callers can pass in a real handler)
  const [toast, setToast] = useState<string | null>(null);
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ── Data fetch ──────────────────────────────────────────────────────────

  useEffect(() => {
    setKeywordsLoading(true);
    Promise.all([
      api.keywords.list(domainId),
      api.domains.getBusinessContext(domainId).catch(() => null),
    ])
      .then(([{ keywords: rows }, biz]) => {
        setKeywords(rows);
        setBizContext(biz);
      })
      .catch(() => setKeywordsError('Failed to load keywords'))
      .finally(() => setKeywordsLoading(false));
  }, [domainId]);

  useEffect(() => {
    setTrendLoading(true);
    api.trends
      .getCategory(domainId)
      .then(setTrend)
      .catch((err: unknown) => {
        if (err instanceof Error && err.message?.includes('404')) {
          setTrendNoData(true);
        }
      })
      .finally(() => setTrendLoading(false));
  }, [domainId]);

  useEffect(() => {
    setBriefsLoading(true);
    Promise.all([api.contentBriefs.list(domainId), api.contentBriefs.quota(domainId)])
      .then(([{ briefs: rows }, q]) => {
        setBriefs(rows);
        setQuota(q);
      })
      .catch(() => setBriefsError('Failed to load content briefs'))
      .finally(() => setBriefsLoading(false));
  }, [domainId]);

  // ── Handlers ────────────────────────────────────────────────────────────

  function handleBriefUpdated(updated: ContentBrief) {
    setBriefs((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
    setSelectedBrief(updated);
  }

  function handleBriefGenerated(brief: ContentBrief) {
    setBriefs((prev) => [brief, ...prev]);
    setGenerateOpen(false);
    setSelectedBrief(brief);
    showToast('Brief generated!');
    // Refresh quota
    api.contentBriefs
      .quota(domainId)
      .then(setQuota)
      .catch(() => undefined);
  }

  const activeBriefs = briefs.filter((b) => b.status !== 'discarded');
  const discardedBriefs = briefs.filter((b) => b.status === 'discarded');
  const displayedBriefs = showDiscarded ? briefs : activeBriefs;

  const canGenerate = tier !== 'free' && (quota?.remaining ?? 0) > 0;

  return (
    <div className="space-y-6">
      {/* ── Toast ────────────────────────────────────────────────────────── */}
      {toast && (
        <div className="fixed top-4 right-4 z-[60] bg-gray-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg animate-fade-in">
          {toast}
        </div>
      )}

      {/* ── Card 1: Keyword Universe ─────────────────────────────────────── */}
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Keyword Universe</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              Researched keywords and their search volume data
            </p>
          </div>
          <LookupForm domainId={domainId} onAdded={(rows) => setKeywords((prev) => mergeKeywords(prev, rows))} />
        </div>

        {keywordsLoading ? (
          <LoadingRows />
        ) : keywordsError ? (
          <ErrorRow message={keywordsError} />
        ) : keywords.length === 0 ? (
          <EmptyRow message="No keywords researched yet. Use the look-up form to add keywords." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-50">
                  <Th>Keyword</Th>
                  <Th right>Volume/mo</Th>
                  <Th right>Competition</Th>
                  <Th right>CPC</Th>
                  <Th right>Geography</Th>
                  <Th right>{''}</Th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((kw) => (
                  <tr key={kw.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50">
                    <td className="px-6 py-3 font-medium text-gray-800">{kw.keyword}</td>
                    <td className="px-6 py-3 text-right text-gray-600">
                      {kw.monthlyVolume !== null ? formatVolume(kw.monthlyVolume) : '—'}
                    </td>
                    <td className="px-6 py-3 text-right">
                      {kw.competition !== null ? (
                        <CompetitionBadge value={kw.competition} />
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-right text-gray-600">
                      {kw.cpc !== null ? `$${kw.cpc.toFixed(2)}` : '—'}
                    </td>
                    <td className="px-6 py-3 text-right text-gray-500 text-xs uppercase">
                      {kw.geography}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <CopyForLlmButton
                        prompt={buildKeywordPrompt(kw, domain, bizContext)}
                        label="Copy"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ── Card 2: Category Demand ──────────────────────────────────────── */}
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50">
          <h2 className="text-base font-semibold text-gray-900">Category Demand</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Google Trends year-over-year interest for this domain's category
          </p>
        </div>

        {trendLoading ? (
          <div className="px-6 py-8">
            <LoadingRows />
          </div>
        ) : trendNoData || !trend ? (
          <EmptyRow message="No trend data yet. This is populated by the daily scheduler once your domain category is identified." />
        ) : (
          <div className="px-6 py-5">
            <div className="flex flex-col sm:flex-row sm:items-start gap-6">
              {/* YoY metric */}
              <div className="shrink-0">
                <div className="text-xs text-gray-500 mb-1">Year-over-year trend index</div>
                <div className="flex items-baseline gap-2">
                  <span
                    className={`text-3xl font-bold ${
                      (trend.trendIndexYoy ?? 0) > 0
                        ? 'text-green-600'
                        : (trend.trendIndexYoy ?? 0) < 0
                          ? 'text-red-500'
                          : 'text-gray-500'
                    }`}
                  >
                    {trend.trendIndexYoy !== null
                      ? `${trend.trendIndexYoy > 0 ? '+' : ''}${trend.trendIndexYoy}`
                      : '—'}
                  </span>
                  {trend.trendIndexYoy !== null && (
                    <span className="text-sm text-gray-500">pts</span>
                  )}
                </div>
                <div className="mt-1 text-xs text-gray-400">
                  {trend.category} · {trend.geography}
                </div>
              </div>

              {/* Sparkline */}
              {trend.interestHistory && trend.interestHistory.length > 1 ? (
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-400 mb-1">Interest over time (weekly)</div>
                  <ResponsiveContainer width="100%" height={64}>
                    <LineChart
                      data={trend.interestHistory.map((h) => ({
                        date: h.weekStart,
                        value: h.interest,
                      }))}
                    >
                      <Line
                        type="monotone"
                        dataKey="value"
                        stroke={
                          (trend.trendIndexYoy ?? 0) >= 0 ? '#16a34a' : '#ef4444'
                        }
                        dot={false}
                        strokeWidth={1.5}
                      />
                      <Tooltip
                        formatter={(value: unknown) => [typeof value === 'number' ? value : '', 'Interest']}
                        labelFormatter={(label: unknown) => (typeof label === 'string' ? label : '')}
                        contentStyle={{ fontSize: 11 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">
                  Insufficient history data
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ── Card 3: Content Briefs ───────────────────────────────────────── */}
      <section className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-gray-900">Content Briefs</h2>
            <p className="text-xs text-gray-500 mt-0.5">
              AI-generated structured briefs for target keywords
            </p>
            {quota && (
              <div className="mt-2 flex items-center gap-2">
                <QuotaBar quota={quota} />
              </div>
            )}
          </div>
          <div className="shrink-0">
            {tier === 'free' ? (
              <span className="text-xs text-gray-400 border border-gray-200 rounded-lg px-3 py-1.5">
                Upgrade to generate briefs
              </span>
            ) : (
              <button
                onClick={() => setGenerateOpen(true)}
                disabled={!canGenerate || briefsLoading}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Generate brief
              </button>
            )}
          </div>
        </div>

        {briefsLoading ? (
          <LoadingRows />
        ) : briefsError ? (
          <ErrorRow message={briefsError} />
        ) : activeBriefs.length === 0 && !showDiscarded ? (
          <EmptyRow message="No content briefs yet. Click 'Generate brief' to create your first one." />
        ) : (
          <div>
            <ul className="divide-y divide-gray-50">
              {displayedBriefs.map((brief) => (
                <BriefRow
                  key={brief.id}
                  brief={brief}
                  domain={domain}
                  biz={bizContext}
                  onOpen={() => setSelectedBrief(brief)}
                />
              ))}
            </ul>
            {discardedBriefs.length > 0 && (
              <div className="px-6 py-3 border-t border-gray-50">
                <button
                  onClick={() => setShowDiscarded((v) => !v)}
                  className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showDiscarded
                    ? `Hide ${discardedBriefs.length} discarded`
                    : `Show ${discardedBriefs.length} discarded`}
                </button>
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── Generate brief modal ─────────────────────────────────────────── */}
      <GenerateBriefModal
        domainId={domainId}
        isOpen={generateOpen}
        onClose={() => setGenerateOpen(false)}
        onGenerated={handleBriefGenerated}
      />

      {/* ── Content brief detail modal ───────────────────────────────────── */}
      <ContentBriefModal
        brief={selectedBrief}
        isOpen={selectedBrief !== null}
        onClose={() => setSelectedBrief(null)}
        onBriefUpdated={handleBriefUpdated}
        onToast={showToast}
      />
    </div>
  );
}

// ── LLM prompt builders ───────────────────────────────────────────────────────

function buildKeywordPrompt(kw: KeywordVolumeEntry, domain: string, biz: BusinessContext | null): string {
  const lines = [`Domain: ${domain}`, `URL: https://${domain}`];
  if (biz) {
    lines.push(`Business: ${biz.coreOffering}${biz.coreOfferingCategory ? ` — ${biz.coreOfferingCategory}` : ''}`);
    lines.push(`Audience: ${biz.targetAudience}`);
    if (biz.geographyCity || biz.geographyCountry) {
      lines.push(`Location: ${[biz.geographyCity, biz.geographyCountry].filter(Boolean).join(', ')}`);
    }
  }
  lines.push('');
  lines.push(`Task: Research and create content targeting the keyword: "${kw.keyword}"`);
  const stats = [
    kw.monthlyVolume !== null && `Monthly volume: ${formatVolume(kw.monthlyVolume)}`,
    kw.competition !== null && `Competition: ${Math.round(kw.competition * 100)}%`,
    kw.cpc !== null && `CPC: $${kw.cpc.toFixed(2)}`,
    kw.geography && `Geography: ${kw.geography.toUpperCase()}`,
  ].filter(Boolean).join(' | ');
  if (stats) lines.push(stats);
  lines.push('');
  lines.push(`Write content that positions ${domain} as the authoritative answer for users searching this keyword. Focus on demonstrating expertise, citing real data, and answering the user's intent clearly.`);
  return lines.join('\n');
}

function buildBriefPrompt(brief: ContentBrief, domain: string, biz: BusinessContext | null): string {
  const lines = [`Domain: ${domain}`, `URL: https://${domain}`];
  if (biz) {
    lines.push(`Business: ${biz.coreOffering}${biz.coreOfferingCategory ? ` — ${biz.coreOfferingCategory}` : ''}`);
    lines.push(`Audience: ${biz.targetAudience}`);
  }
  lines.push('');
  lines.push(`Task: Write content for keyword: "${brief.targetKeyword}"`);
  if (brief.suggestedTitle) lines.push(`Suggested title: ${brief.suggestedTitle}`);
  if (brief.metaDescription) lines.push(`Meta description: ${brief.metaDescription}`);
  if (brief.headings && brief.headings.length > 0) {
    lines.push('');
    lines.push('Suggested headings:');
    brief.headings.forEach((h, i) => lines.push(`${i + 1}. ${h}`));
  }
  lines.push('');
  lines.push('Use this brief to write a complete, authoritative article. Follow the suggested structure, address the target keyword naturally, and ensure the content demonstrates real expertise that AI platforms and search engines will cite.');
  return lines.join('\n');
}

// ── Brief row ─────────────────────────────────────────────────────────────────

const BRIEF_STATUS_COLORS: Record<string, string> = {
  drafted: 'bg-zinc-100 text-zinc-600',
  approved: 'bg-green-100 text-green-700',
  article_being_written: 'bg-blue-100 text-blue-700',
  article_published: 'bg-indigo-100 text-indigo-700',
  discarded: 'bg-red-50 text-red-500',
};

const BRIEF_STATUS_LABELS: Record<string, string> = {
  drafted: 'Drafted',
  approved: 'Approved',
  article_being_written: 'Writing',
  article_published: 'Published',
  discarded: 'Discarded',
};

function BriefRow({
  brief,
  domain,
  biz,
  onOpen,
}: {
  brief: ContentBrief;
  domain: string;
  biz: BusinessContext | null;
  onOpen: () => void;
}) {
  return (
    <li className="px-6 py-3 flex items-center justify-between gap-3 hover:bg-gray-50/50">
      <div className="min-w-0 flex-1 cursor-pointer" onClick={onOpen}>
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-medium text-gray-800 truncate">
            {brief.suggestedTitle ?? brief.targetKeyword}
          </span>
          {brief.suggestedTitle && (
            <span className="text-xs text-gray-400 shrink-0 font-mono">
              {brief.targetKeyword}
            </span>
          )}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">
          Generated {new Date(brief.generatedAt).toLocaleDateString()}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <CopyForLlmButton prompt={buildBriefPrompt(brief, domain, biz)} label="Copy" />
        <span
          className={`inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full ${BRIEF_STATUS_COLORS[brief.status] ?? 'bg-gray-100 text-gray-500'}`}
        >
          {BRIEF_STATUS_LABELS[brief.status] ?? brief.status}
        </span>
      </div>
    </li>
  );
}

// ── Quota bar ─────────────────────────────────────────────────────────────────

function QuotaBar({ quota }: { quota: ContentBriefQuota }) {
  const pct = quota.allowance > 0 ? Math.round((quota.used / quota.allowance) * 100) : 0;
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${pct >= 90 ? 'bg-red-400' : 'bg-blue-400'}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
      <span className="text-xs text-gray-500">
        {quota.used} / {quota.allowance} briefs this month
      </span>
    </div>
  );
}

// ── Keyword lookup form ───────────────────────────────────────────────────────

function LookupForm({
  domainId,
  onAdded,
}: {
  domainId: string;
  onAdded: (rows: KeywordVolumeEntry[]) => void;
}) {
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const kws = input
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (kws.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const { keywords } = await api.keywords.lookup(domainId, kws);
      onAdded(keywords);
      setInput('');
    } catch {
      setError('Lookup failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2">
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="keyword1, keyword2…"
        className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 w-44 focus:outline-none focus:ring-1 focus:ring-blue-300"
        aria-label="Keywords to look up"
      />
      <button
        type="submit"
        disabled={loading || !input.trim()}
        className="text-xs px-3 py-1.5 bg-gray-900 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 transition-colors"
      >
        {loading ? '…' : 'Look up'}
      </button>
      {error && <span className="text-xs text-red-500">{error}</span>}
    </form>
  );
}

// ── Generate brief modal ──────────────────────────────────────────────────────

interface GenerateBriefModalProps {
  domainId: string;
  isOpen: boolean;
  onClose: () => void;
  onGenerated: (brief: ContentBrief) => void;
}

function GenerateBriefModal({
  domainId,
  isOpen,
  onClose,
  onGenerated,
}: GenerateBriefModalProps) {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setKeyword('');
      setError(null);
      setLoading(false);
    }
  }, [isOpen]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!keyword.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const brief = await api.contentBriefs.generate(domainId, keyword.trim());
      onGenerated(brief);
    } catch (err) {
      const msg =
        err instanceof Error
          ? err.message.includes('429')
            ? 'Monthly quota reached or a brief is already being generated'
            : err.message
          : 'Failed to generate brief';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="gen-brief-title"
      >
        <h2 id="gen-brief-title" className="text-base font-semibold text-gray-900 mb-4">
          Generate content brief
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="gen-brief-keyword" className="block text-xs font-medium text-gray-500 mb-1.5">
              Target keyword
            </label>
            <input
              id="gen-brief-keyword"
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              maxLength={200}
              placeholder="e.g. best seo tools for small business"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-300"
              autoFocus
            />
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 border border-red-100">
              {error}
            </div>
          )}

          {loading && (
            <div className="text-xs text-gray-500 bg-blue-50 rounded-lg px-3 py-2">
              Generating brief with Claude Sonnet — this takes about 15–30 seconds…
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-1.5 text-sm border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !keyword.trim()}
              className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Generating…' : 'Generate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Shared UI helpers ─────────────────────────────────────────────────────────

function Th({ children, right }: { children: React.ReactNode; right?: boolean }) {
  return (
    <th
      className={`px-6 py-2.5 text-[11px] font-medium uppercase tracking-wide text-gray-400 ${right ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function LoadingRows() {
  return (
    <div className="px-6 py-8 space-y-2">
      {[1, 2, 3].map((i) => (
        <div key={i} className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: `${80 - i * 10}%` }} />
      ))}
    </div>
  );
}

function EmptyRow({ message }: { message: string }) {
  return (
    <div className="px-6 py-8 text-center text-sm text-gray-400">{message}</div>
  );
}

function ErrorRow({ message }: { message: string }) {
  return (
    <div className="px-6 py-8 text-center text-sm text-red-500">{message}</div>
  );
}

function CompetitionBadge({ value }: { value: number }) {
  // value is 0-1 from DataForSEO
  const pct = Math.round(value * 100);
  const color =
    pct < 34 ? 'text-green-600' : pct < 67 ? 'text-amber-600' : 'text-red-500';
  return <span className={`font-medium ${color}`}>{pct}%</span>;
}

function formatVolume(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}

function mergeKeywords(existing: KeywordVolumeEntry[], incoming: KeywordVolumeEntry[]): KeywordVolumeEntry[] {
  const map = new Map(existing.map((k) => [k.id, k]));
  for (const k of incoming) {
    map.set(k.id, k);
  }
  return Array.from(map.values());
}
